import { useEffect, useRef, useState } from "react";
import "./DemoVertical.css";

/**
 * Vertical 9:16 demo page for screen recording (OBS, Camtasia, ScreenPal).
 * Loops through 8 scenes (intro + 6 product scenes + outro CTA), ~7s each =
 * ~56s total. Big captions, slow transitions, no chrome — record at 1080×1920
 * and export directly to Reels/TikTok/Shorts.
 *
 * NARRATION: OpenAI TTS (voz Onyx) sintetizado server-side e cacheado em disco
 * via `/api/demo/narration/{scene_id}`. Áudio precisa de gesto do usuário para
 * tocar (browser autoplay policy), por isso adicionamos o botão "Ativar
 * narração" no painel — uma vez clicado, o áudio toca em sync com cada cena.
 */
const SCENES = [
  {
    id: "intro",
    duration: 5500,
    lead: "NR-01 · 2025/2026",
    title: "A nova era da gestão psicossocial.",
    sub: "Conheça a NR01 Sollute",
  },
  {
    id: "risk",
    duration: 6500,
    lead: "Riscos invisíveis",
    title: "Quantos riscos psicossociais existem na sua operação hoje?",
  },
  {
    id: "phone",
    duration: 5500,
    lead: "Aplicação simples",
    title: "Questionário no celular do colaborador.",
    sub: "54 perguntas · anônimo · em minutos",
  },
  {
    id: "dashboard",
    duration: 6000,
    lead: "Inteligência em tempo real",
    title: "Risco por setor, em tempo real.",
  },
  {
    id: "ai",
    duration: 5500,
    lead: "IA especializada",
    title: "Planos de ação gerados automaticamente.",
  },
  {
    id: "paper",
    duration: 5500,
    lead: "Conformidade MTE",
    title: "Relatório AEP pronto para auditoria.",
    sub: "PDF · Word · Excel · em um clique",
  },
  {
    id: "outro",
    duration: 7000,
    lead: "",
    title: "",
  },
];

const API = process.env.REACT_APP_BACKEND_URL;

export default function DemoVertical() {
  const [active, setActive] = useState(0);
  const [hideUI, setHideUI] = useState(false);
  const [audioOn, setAudioOn] = useState(false);
  const audioRef = useRef(null);

  // Advance scenes (independent of audio so the visuals never stall)
  useEffect(() => {
    const t = setTimeout(() => {
      setActive((s) => (s + 1) % SCENES.length);
    }, SCENES[active].duration);
    return () => clearTimeout(t);
  }, [active]);

  // Play narration for the current scene whenever audio is enabled.
  // The src changes trigger the browser to load+play the new clip.
  useEffect(() => {
    if (!audioOn || !audioRef.current) return;
    const url = `${API}/api/demo/narration/${SCENES[active].id}`;
    audioRef.current.src = url;
    // catch() needed because some browsers reject the play promise if the tab
    // is backgrounded or the audio failed to decode.
    audioRef.current.play().catch(() => {});
  }, [active, audioOn]);

  // Press F to fullscreen, H to hide UI, M to toggle mute
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "f" || e.key === "F") document.documentElement.requestFullscreen?.();
      if (e.key === "h" || e.key === "H") setHideUI((v) => !v);
      if (e.key === "m" || e.key === "M") setAudioOn((v) => !v);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const cur = SCENES[active];

  return (
    <div className="vd-root" data-testid="demo-vertical">
      {/* Hidden audio element — controlled programmatically */}
      <audio ref={audioRef} preload="auto" data-testid="vd-audio" />

      <div className="vd-stage">
        <div className="vd-bar-top" />
        <div className="vd-bar-bottom" />

        {!hideUI && cur.id !== "outro" && (
          <div className="vd-brand">
            <span className="dot"></span> NR01 Sollute
          </div>
        )}

        <div className="vd-particles" aria-hidden="true">
          {Array.from({ length: 18 }).map((_, i) => (
            <span
              key={i}
              style={{
                left: `${(i * 6 + 3) % 95}%`,
                animationDelay: `${(i * 0.4) % 8}s`,
                animationDuration: `${7 + (i % 4)}s`,
              }}
            />
          ))}
        </div>
        <div className="vd-scan" aria-hidden="true" />

        {/* ===== INTRO ===== */}
        <div className={`vd-scene ${cur.id === "intro" ? "is-active" : ""}`} key={cur.id === "intro" ? "intro-live" : "intro-off"}>
          <div className="vd-intro">
            <div className="ring">
              <div className="logo">S</div>
            </div>
            <h1>NR01 Sollute</h1>
            <p className="sub">Plataforma inteligente para fatores psicossociais</p>
          </div>
        </div>

        {/* ===== RISK ===== */}
        <div className={`vd-scene ${cur.id === "risk" ? "is-active" : ""}`}>
          <div className="vd-risk-grid">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className={`vd-risk-cell ${[1, 3, 7].includes(i) ? "alert" : ""}`}></div>
            ))}
          </div>
        </div>

        {/* ===== PHONE ===== */}
        <div className={`vd-scene ${cur.id === "phone" ? "is-active" : ""}`} key={cur.id === "phone" ? "phone-live" : "phone-off"}>
          <div className="vd-phone">
            <div className="head">Questionário NR-01 · 32/54</div>
            <div className="progress"><div></div></div>
            <div className="q">O ritmo de trabalho é intenso durante a maior parte da jornada?</div>
            <div className="opt">Nunca</div>
            <div className="opt">Raramente</div>
            <div className="opt sel">Às vezes</div>
            <div className="opt">Frequentemente</div>
            <div className="opt">Sempre</div>
          </div>
        </div>

        {/* ===== DASHBOARD ===== */}
        <div className={`vd-scene ${cur.id === "dashboard" ? "is-active" : ""}`} key={cur.id === "dashboard" ? "dash-live" : "dash-off"}>
          <div className="vd-dash">
            <div className="head">
              <div>
                <div className="label">Risco médio · tempo real</div>
                <div className="co">Empresa Beta · GHE-04</div>
              </div>
              <div className="num">54<span style={{ fontSize: 18, opacity: 0.6 }}>%</span></div>
            </div>
            {[
              ["Demandas", 78, "linear-gradient(90deg,#dc2626,#f87171)"],
              ["Controle", 62, "linear-gradient(90deg,#ea580c,#fb923c)"],
              ["Liderança", 45, "linear-gradient(90deg,#eab308,#facc15)"],
              ["Relações", 31, "linear-gradient(90deg,#22c55e,#86efac)"],
              ["Ambiente", 57, "linear-gradient(90deg,#ea580c,#fb923c)"],
            ].map(([name, val, bg]) => (
              <div className="row" key={name}>
                <span className="name">{name}</span>
                <div className="bar"><div style={{ width: `${val}%`, background: bg }}></div></div>
                <span className="val">{val}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* ===== AI ===== */}
        <div className={`vd-scene ${cur.id === "ai" ? "is-active" : ""}`} key={cur.id === "ai" ? "ai-live" : "ai-off"}>
          <div className="vd-ai">
            <svg viewBox="0 0 380 380">
              <path d="M50,50 L190,190 L50,330" />
              <path d="M330,50 L190,190 L330,330" />
              <path d="M190,50 L190,330" />
              <path d="M50,190 L330,190" />
            </svg>
            <span className="node big" style={{ top: "calc(50% - 24px)", left: "calc(50% - 24px)" }}></span>
            <span className="node" style={{ top: "10%", left: "10%" }}></span>
            <span className="node" style={{ top: "10%", right: "10%" }}></span>
            <span className="node" style={{ bottom: "10%", left: "10%" }}></span>
            <span className="node" style={{ bottom: "10%", right: "10%" }}></span>
            <span className="node" style={{ top: "6%", left: "50%", marginLeft: "-11px" }}></span>
            <span className="node" style={{ bottom: "6%", left: "50%", marginLeft: "-11px" }}></span>
          </div>
        </div>

        {/* ===== AEP PAPER ===== */}
        <div className={`vd-scene ${cur.id === "paper" ? "is-active" : ""}`} key={cur.id === "paper" ? "paper-live" : "paper-off"}>
          <div className="vd-paper">
            <div className="head">Relatório AEP · NR-01</div>
            <div className="ttl">Avaliação dos Fatores Psicossociais</div>
            <div className="lines">
              <span></span><span></span><span></span><span></span><span></span><span></span>
            </div>
            <div className="badge">Conforme MTE</div>
            <div className="seal"><i className="fa-solid fa-check"></i></div>
          </div>
        </div>

        {/* ===== OUTRO CTA ===== */}
        <div className={`vd-scene ${cur.id === "outro" ? "is-active" : ""}`} key={cur.id === "outro" ? "outro-live" : "outro-off"}>
          <div className="vd-outro">
            <div className="logo">S</div>
            <h1>Inteligência estratégica para SST.</h1>
            <div className="url">nr01sollute.com</div>
            <div className="cta">Solicite uma demo</div>
          </div>
        </div>

        {/* Caption (skip on intro/outro — they have their own) */}
        {cur.id !== "intro" && cur.id !== "outro" && (
          <div className="vd-caption" key={`cap-${cur.id}`}>
            <span className="lead">{cur.lead}</span>
            <h2>{cur.title}</h2>
            {cur.sub && <p>{cur.sub}</p>}
          </div>
        )}
      </div>

      {/* Recording control panel — press H to toggle */}
      <div className={`vd-controls ${hideUI ? "hidden" : ""}`} data-testid="vd-controls">
        <div className="title">Modo gravação</div>
        <div>Pressione <strong>F</strong> tela cheia · <strong>H</strong> ocultar · <strong>M</strong> mute</div>
        <div style={{ marginTop: 8, opacity: 0.7, fontSize: 11 }}>
          1 loop completo ≈ 41s · grave em 1080×1920 e poste em Reels/TikTok.
        </div>
        <button onClick={() => document.documentElement.requestFullscreen?.()} data-testid="vd-fullscreen">Tela cheia</button>
        <button
          onClick={() => {
            const nxt = !audioOn;
            setAudioOn(nxt);
            // Trigger immediate playback for the current scene on enable
            if (nxt && audioRef.current) {
              audioRef.current.src = `${API}/api/demo/narration/${SCENES[active].id}`;
              audioRef.current.play().catch(() => {});
            } else if (audioRef.current) {
              audioRef.current.pause();
            }
          }}
          data-testid="vd-audio-toggle"
          style={{ background: audioOn ? "#dc2626" : "#8BC53F", color: audioOn ? "white" : "#0E1A14" }}
        >
          {audioOn ? <><i className="fa-solid fa-volume-xmark"></i> Mute</> : <><i className="fa-solid fa-volume-high"></i> Ativar narração</>}
        </button>
        <button onClick={() => setHideUI(true)} data-testid="vd-hide">Ocultar (H)</button>
      </div>
    </div>
  );
}

